import React, {useEffect, useMemo, useState} from "react";
import Button from "react-bootstrap/Button";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import {Col, Row} from "react-bootstrap";
import OpenCurrency from "./quick-actions/OpenCurrency";
import { FaArrowLeft, FaArrowRight, FaEye, FaEyeSlash, FaLayerGroup } from 'react-icons/fa';
import TradingViewChart from "./TradingViewChart";
import OverlaySettingsModal from "./OverlaySettingsModal";
import { buildHistoricalEventOverlay } from "./historicalEventOverlay";
import useInsightsHistory from "../history/useInsightsHistory";
import useChartEvaluationHistory from "../chartEvaluation/useChartEvaluationHistory";
import { buildCodexEvaluationMarkers, buildSharedEvaluationMarkers, formatCodexMarkerTime } from "../codex/codexEvaluationView";

const OVERLAY_SETTINGS_KEY = "chartOverlaySettings";
const DEFAULT_OVERLAY_SETTINGS = {
    customDrawings: true,
    historicalEvents: false,
    eventTimeframe: "",
    lookback: "7d",
    btcRegime: false,
    previousDayLevels: true,
};

const loadOverlaySettings = () => {
    try {
        const stored = JSON.parse(localStorage.getItem(OVERLAY_SETTINGS_KEY));
        return { ...DEFAULT_OVERLAY_SETTINGS, ...(stored || {}) };
    } catch {
        return { ...DEFAULT_OVERLAY_SETTINGS };
    }
};

const saveOverlaySettings = (settings) => {
    try {
        localStorage.setItem(OVERLAY_SETTINGS_KEY, JSON.stringify(settings));
    } catch {}
};

function Graph({ selectedEventData, onEventChange, onTradeEvent, onTimeframeChange, btcRegime, compactMobile, onCodexEvaluationSelect, assessmentContext, enableTradeCommands = false }) {

    const [eventInfo, setEventInfo] = useState(null);
    const [activeTimeframe, setActiveTimeframe] = useState(null);
    const [overlaySettings, setOverlaySettings] = useState(loadOverlaySettings);
    const [overlayDraft, setOverlayDraft] = useState(loadOverlaySettings);
    const [showOverlaySettings, setShowOverlaySettings] = useState(false);
    const [customDrawingsHidden, setCustomDrawingsHidden] = useState(false);
    const shouldLoadHistory = !assessmentContext && (showOverlaySettings || overlaySettings.historicalEvents);
    const {
        data: history,
        error: historyError,
    } = useInsightsHistory(shouldLoadHistory);
    const chartEvaluationSymbol = selectedEventData?.chartMarket?.symbol
        || (eventInfo?.cur && eventInfo?.base ? `${eventInfo.cur}${eventInfo.base}` : eventInfo?.cur ? `${eventInfo.cur}USDT` : "");
    const { data: sharedEvaluationHistory } = useChartEvaluationHistory(
        chartEvaluationSymbol, !assessmentContext && Boolean(chartEvaluationSymbol));

    useEffect(() => {
        const evData = selectedEventData?.sectionEvents[selectedEventData?.index];
        let timeFrame = assessmentContext ? null : new URLSearchParams(window.location.search)?.get("timeframe");
        if (!timeFrame) { timeFrame = String(evData?.tf || "").replace(/^_/, ""); }
        setEventInfo(evData);
        setActiveTimeframe(timeFrame);
        onTimeframeChange?.(timeFrame);
        setReloadKey((prevKey) => prevKey + 1);
    }, [assessmentContext?.id, selectedEventData, selectedEventData?.index]);

    useEffect(() => {
        setCustomDrawingsHidden(false);
    }, [assessmentContext?.id, eventInfo?.cur]);

    const [reloadKey, setReloadKey] = useState(0);
    const handleTimeframeChange = (tf) => {
        setActiveTimeframe(tf);
        onTimeframeChange?.(tf);
    };

    const handleReloadGraph = () => {
        setReloadKey((prevKey) => prevKey + 1);
    };

    const openOverlaySettings = () => {
        setOverlayDraft({
            ...overlaySettings,
            eventTimeframe: overlaySettings.eventTimeframe || activeTimeframe || "15M",
        });
        setShowOverlaySettings(true);
    };

    const applyOverlaySettings = () => {
        setOverlaySettings(overlayDraft);
        saveOverlaySettings(overlayDraft);
        setShowOverlaySettings(false);
    };

    const clearOverlaySettings = () => {
        const cleared = {
            ...overlayDraft,
            customDrawings: false,
            historicalEvents: false,
            btcRegime: false,
            previousDayLevels: false,
        };
        setOverlayDraft(cleared);
        setOverlaySettings(cleared);
        saveOverlaySettings(cleared);
        setShowOverlaySettings(false);
    };

    const historicalEventOverlay = useMemo(() => {
        if (assessmentContext || !overlaySettings.historicalEvents || !history || !eventInfo?.cur) {
            return { drawings: [], eventCount: 0, markerCount: 0 };
        }
        return buildHistoricalEventOverlay({
            history,
            currency: eventInfo.cur,
            eventTimeframe: overlaySettings.eventTimeframe,
            chartTimeframe: activeTimeframe,
            lookback: overlaySettings.lookback,
        });
    }, [activeTimeframe, assessmentContext, eventInfo?.cur, history, overlaySettings]);

    const historicalEventPreview = useMemo(() => {
        if (!showOverlaySettings || !overlayDraft.historicalEvents || !history || !eventInfo?.cur) {
            return { drawings: [], eventCount: 0, markerCount: 0 };
        }
        const draftMatchesApplied =
            overlaySettings.historicalEvents &&
            overlayDraft.eventTimeframe === overlaySettings.eventTimeframe &&
            overlayDraft.lookback === overlaySettings.lookback;
        if (draftMatchesApplied) {
            return historicalEventOverlay;
        }
        return buildHistoricalEventOverlay({
            history,
            currency: eventInfo.cur,
            eventTimeframe: overlayDraft.eventTimeframe,
            chartTimeframe: activeTimeframe,
            lookback: overlayDraft.lookback,
        });
    }, [activeTimeframe, eventInfo?.cur, historicalEventOverlay, history, overlayDraft, overlaySettings, showOverlaySettings]);

    const overlaysActive = overlaySettings.customDrawings || overlaySettings.historicalEvents || overlaySettings.btcRegime;
    const customDrawingsVisible = assessmentContext
        ? !customDrawingsHidden : overlaySettings.customDrawings && !customDrawingsHidden;

    const catchDrawings = useMemo(() => {
        const catchTrade = eventInfo?.catchTrade;
        const baseline = Number(catchTrade?.currentBaseline);
        if (!catchTrade || !Number.isFinite(baseline) || baseline <= 0) return [];
        const start = Number(catchTrade.submittedAt) || Math.floor(Date.now() / 1000);
        const activationPercent = Number(catchTrade.activationPercent) || 1;
        const activation = catchTrade.side === "SHORT"
            ? baseline * (1 - activationPercent / 100)
            : baseline * (1 + activationPercent / 100);
        const line = (price, color, style, key) => ({
            overlayKey: `catch-${catchTrade.id}-${key}`,
            overlaySignature: `${price}-${catchTrade.status}`,
            point: [{ time: start, price }],
            options: {
                shape: "horizontal_ray",
                disableSelection: false,
                disableSave: true,
                lock: false,
                overrides: { linecolor: color, textcolor: color, linestyle: style, linewidth: 1 },
                zOrder: "top"
            }
        });
        const drawings = [
            line(baseline, "rgba(56, 189, 248, 0.9)", 0, "baseline"),
            line(activation, "rgba(125, 211, 252, 0.48)", 2, "activation")
        ];
        const candidate = Number(catchTrade.candidateBaseline);
        if (Number.isFinite(candidate) && candidate > 0) {
            drawings.push(line(candidate, "rgba(251, 191, 36, 0.78)", 2, "candidate"));
        }
        return drawings;
    }, [eventInfo?.catchTrade]);

    const chartOverlayDrawings = useMemo(
        () => [...historicalEventOverlay.drawings, ...catchDrawings],
        [catchDrawings, historicalEventOverlay.drawings]
    );

    const latestCodexEvaluation = assessmentContext?.evaluation || eventInfo?.codexEvaluation || null;
    const codexEvaluationMarkers = useMemo(() => buildCodexEvaluationMarkers({
        latest: latestCodexEvaluation,
        history: eventInfo?.codexEvaluations || [],
        interval: activeTimeframe,
        eventPrice: eventInfo?.price,
    }), [activeTimeframe, eventInfo?.codexEvaluations, eventInfo?.price, latestCodexEvaluation]);
    const sharedEvaluationMarkers = useMemo(() => assessmentContext ? [] : buildSharedEvaluationMarkers({
        records: sharedEvaluationHistory?.records || [],
        interval: activeTimeframe,
        eventPrice: eventInfo?.price,
    }), [activeTimeframe, assessmentContext, eventInfo?.price, sharedEvaluationHistory?.records]);
    const allEvaluationMarkers = useMemo(() => {
        if (assessmentContext) return [];
        const sharedAttempts = new Set((sharedEvaluationHistory?.records || []).map((record) => record.attemptId).filter(Boolean));
        return [
            ...codexEvaluationMarkers.filter((marker) => !sharedAttempts.has(marker.evaluation?.attemptId)),
            ...sharedEvaluationMarkers,
        ];
    }, [assessmentContext, codexEvaluationMarkers, sharedEvaluationHistory?.records, sharedEvaluationMarkers]);
    const latestSharedEvaluation = assessmentContext ? null : sharedEvaluationHistory?.records?.[0];
    const latestChartEvaluation = assessmentContext?.evaluation || (latestSharedEvaluation
        ? { ...latestSharedEvaluation.result, evaluatedAt: latestSharedEvaluation.completedAt, _sharedRecord: latestSharedEvaluation }
        : latestCodexEvaluation);

    const assessmentLevels = useMemo(() => {
        if (!assessmentContext?.evaluation) return null;
        const evaluation = assessmentContext.evaluation;
        return Object.fromEntries(["entry", "stopLoss", "takeProfit1", "takeProfit2"]
            .map((key) => [key, Number(evaluation[key])])
            .filter(([, value]) => Number.isFinite(value) && value > 0));
    }, [assessmentContext]);

    const moveTo = (val) => {
        const len = selectedEventData.sectionEvents.length;
        let next = selectedEventData.index + val;
        next = next % len;
        if (next < 0) next = len - 1;
        onEventChange({ ...selectedEventData, index: next });
    }

    const handleChartReady = () => {
        onTradeEvent?.({ data: { type: "chart_ready" }});
    };

    const handleChartFailed = () => {
        onTradeEvent?.({ data: { type: "chart_failed" }});
    };

    return (selectedEventData?.sectionEvents && (
        <div>
            <Row className={compactMobile ? "flex-nowrap align-items-center g-1" : "chartDesktopToolbar"}>
                <Col
                    md={compactMobile ? undefined : 6}
                    className={compactMobile ? "flex-shrink-0" : "chartDesktopControlArea"}
                    style={compactMobile ? { width: "100%", maxWidth: "100%" } : undefined}
                >
                    <div className="chartToolbarControls" style={compactMobile ? { width: "100%" } : undefined}>
                        <ButtonGroup aria-label="Timeframe" style={compactMobile ? { gap: 0, flex: 1, display: "flex" } : undefined}>
                            {["5M", "15M", "4H", "1D", "1W"].map((tf) => (
                                <Button
                                    key={tf}
                                    variant={activeTimeframe === tf ? "primary" : "secondary"}
                                    onClick={() => handleTimeframeChange(tf)}
                                    style={compactMobile ? { padding: "8px 6px", fontSize: "0.9rem", flex: 1 } : undefined}
                                >
                                    {tf}
                                </Button>
                            ))}
                            <Button variant="" id="graphRefreshButton" onClick={handleReloadGraph}>
                                <button id="refreshButton"></button>
                            </Button>
                        </ButtonGroup>
                        {assessmentContext && <span className="assessmentChartTf">Chart TF {activeTimeframe || "—"}</span>}
                        {!assessmentContext && <Button
                            variant=""
                            className={overlaysActive ? "chartOverlaysToggle chartOverlaysToggleActive" : "chartOverlaysToggle"}
                            onClick={openOverlaySettings}
                            title="Choose chart overlays"
                            aria-label="Choose chart overlays"
                            aria-pressed={overlaysActive}
                            style={compactMobile ? { flex: "0 0 42px", padding: "6px 4px" } : undefined}
                        >
                            <FaLayerGroup aria-hidden="true" />
                            {!compactMobile && <span className="chartOverlaysLabel">Overlays</span>}
                            {overlaysActive && <span className="chartOverlaysActiveDot" aria-hidden="true" />}
                        </Button>}
                        {latestChartEvaluation && (
                            <Button
                                variant=""
                                className="codexChartAnalysisBadge"
                                onClick={() => onCodexEvaluationSelect?.(latestChartEvaluation)}
                                title="Read the latest chart evaluation"
                                aria-label={`Read chart evaluation from ${formatCodexMarkerTime(latestChartEvaluation.evaluatedAt)} UTC`}
                            >
                                <span>{(assessmentContext?.provider || latestSharedEvaluation?.provider) === "CUSTOM_AI" ? "Custom AI" : "Codex"}</span>
                                {!compactMobile && <small>{formatCodexMarkerTime(latestChartEvaluation.evaluatedAt)} UTC</small>}
                            </Button>
                        )}
                        <Button
                            variant=""
                            className={customDrawingsHidden
                                ? "chartDrawingsVisibilityToggle chartDrawingsVisibilityToggleHidden"
                                : "chartDrawingsVisibilityToggle"}
                            onClick={() => setCustomDrawingsHidden((hidden) => !hidden)}
                            title={customDrawingsHidden ? (assessmentContext ? "Show event drawings" : "Show custom drawings") : (assessmentContext ? "Hide event drawings" : "Hide custom drawings")}
                            aria-label={customDrawingsHidden ? (assessmentContext ? "Show event drawings" : "Show custom drawings") : (assessmentContext ? "Hide event drawings" : "Hide custom drawings")}
                            aria-pressed={customDrawingsHidden}
                            disabled={!assessmentContext && !overlaySettings.customDrawings}
                        >
                            {customDrawingsHidden
                                ? <FaEyeSlash aria-hidden="true" />
                                : <FaEye aria-hidden="true" />}
                        </Button>
                    </div>
                </Col>

                {!compactMobile && (
                    <Col md={6} className="chartDesktopActionArea">
                        <Row className="align-items-center g-2 flex-nowrap">
                            { selectedEventData.sectionEvents.length > 1 && (
                                <Col md={3} className="chartDesktopEventNavigation">
                                    <Button onClick={() => { moveTo(-1) }} aria-label="Previous event">
                                        <FaArrowLeft />
                                    </Button>
                                    <span>{selectedEventData.index + 1} / {selectedEventData.sectionEvents.length}</span>
                                    <Button onClick={() => { moveTo(1) }} aria-label="Next event">
                                        <FaArrowRight />
                                    </Button>
                                </Col>
                            )}
                            <Col md={9} className="chartDesktopOpenCurrency">
                                <OpenCurrency formClass="chartDesktopOpenCurrencyForm"/>
                            </Col>
                        </Row>
                    </Col>
                )}
            </Row>

            {compactMobile && selectedEventData.sectionEvents.length > 1 && (
                <Row className="align-items-center g-1 mt-1">
                    <Col className="d-flex align-items-center gap-1">
                        <Button style={{ padding: "6px 12px" }} onClick={() => { moveTo(-1) }}><FaArrowLeft /></Button>
                        <span style={{ color: 'white', fontSize: '0.9rem' }}>{selectedEventData.index + 1} / {selectedEventData.sectionEvents.length}</span>
                        <Button style={{ padding: "6px 12px" }} onClick={() => { moveTo(1) }}><FaArrowRight /></Button>
                    </Col>
                </Row>
            )}

            <div className="mt-2">
                {eventInfo && (
                    <TradingViewChart
                        key={reloadKey}
                        currency={eventInfo?.cur}
                        base={eventInfo?.base || "USDT"}
                        interval={activeTimeframe}
                        timezone="Etc/UTC"
                        drawParam={eventInfo?.draw || (assessmentContext ? null : new URLSearchParams(window.location.search)?.get("draw"))}
                        historicalEventDrawings={chartOverlayDrawings}
                        codexEvaluationMarkers={allEvaluationMarkers}
                        btcRegime={btcRegime}
                        showBtcRegime={overlaySettings.btcRegime}
                        showPreviousDayLevels={overlaySettings.previousDayLevels}
                        showCustomDrawings={customDrawingsVisible}
                        focusLatest={Boolean(eventInfo?.focusLatest)}
                        focusTime={assessmentContext?.focusTime}
                        assessmentLevels={assessmentLevels}
                        showAssessmentLevels
                        assessmentMode={Boolean(assessmentContext)}
                        enableTradeCommands={enableTradeCommands}
                        theme={localStorage.getItem("theme") || "dark"}
                        isSpot={false}
                        onChartReady={handleChartReady}
                        onChartFailed={handleChartFailed}
                        onCodexEvaluationSelect={onCodexEvaluationSelect}
                    />
                )}
            </div>

            {!assessmentContext && <OverlaySettingsModal
                show={showOverlaySettings}
                onHide={() => setShowOverlaySettings(false)}
                settings={overlayDraft}
                onChange={setOverlayDraft}
                onApply={applyOverlaySettings}
                onClear={clearOverlaySettings}
                historyLoading={overlayDraft.historicalEvents && !history && !historyError}
                historyError={historyError}
                eventCount={historicalEventPreview.eventCount}
                markerCount={historicalEventPreview.markerCount}
            />}
        </div>
    ));
}

export default Graph;

